import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Save, Trash2, Filter } from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

export default function FilterDrawer({ isOpen, onClose, onApplyFilters, projectId }) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assigned_to: '',
    date_from: '',
    date_to: '',
    tags: ''
  });
  const [savedFilters, setSavedFilters] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [users, setUsers] = useState([]);

  // Load saved filters and users
  useEffect(() => {
    if (isOpen) {
      loadSavedFilters();
      loadUsers();
    }
  }, [isOpen]);

  const loadSavedFilters = async () => {
    try {
      const response = await axiosInstance.get('/saved-filters');
      setSavedFilters(response.data);
    } catch (error) {
      console.error('Error loading saved filters:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axiosInstance.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleClear = () => {
    const emptyFilters = {
      status: '',
      priority: '',
      assigned_to: '',
      date_from: '',
      date_to: '',
      tags: ''
    };
    setFilters(emptyFilters);
    onApplyFilters(emptyFilters);
  };

  const handleSaveFilter = async () => {
    if (!filterName.trim()) {
      toast.error('Lütfen filtre adı girin');
      return;
    }

    try {
      await axiosInstance.post('/saved-filters', {
        name: filterName,
        filters: filters
      });
      toast.success('Filtre kaydedildi!');
      setFilterName('');
      setShowSaveDialog(false);
      loadSavedFilters();
    } catch (error) {
      toast.error('Filtre kaydedilemedi');
    }
  };

  const handleLoadFilter = (savedFilter) => {
    setFilters(savedFilter.filters);
    toast.success(`"${savedFilter.name}" filtreleri yüklendi`);
  };

  const handleDeleteFilter = async (filterId) => {
    try {
      await axiosInstance.delete(`/saved-filters/${filterId}`);
      toast.success('Filtre silindi');
      loadSavedFilters();
    } catch (error) {
      toast.error('Filtre silinemedi');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full md:w-96 bg-white dark:bg-gray-900 shadow-xl z-50 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            <h2 className="text-lg font-semibold">{t('advancedFilters')}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Saved Filters */}
        {savedFilters.length > 0 && (
          <div className="p-4 border-b">
            <h3 className="text-sm font-medium mb-3">{t('savedFilters')}</h3>
            <div className="space-y-2">
              {savedFilters.map((savedFilter) => (
                <div
                  key={savedFilter.id}
                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <button
                    onClick={() => handleLoadFilter(savedFilter)}
                    className="flex-1 text-left text-sm hover:text-blue-600 transition-colors"
                  >
                    {savedFilter.name}
                  </button>
                  <button
                    onClick={() => handleDeleteFilter(savedFilter.id)}
                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters Form */}
        <div className="p-4 space-y-4">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('status')}</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:bg-gray-800"
            >
              <option value="">{t('all')}</option>
              <option value="todo">{t('todo')}</option>
              <option value="in-progress">{t('inProgress')}</option>
              <option value="done">{t('done')}</option>
            </select>
          </div>

          {/* Priority Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('priority')}</label>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:bg-gray-800"
            >
              <option value="">{t('all')}</option>
              <option value="low">{t('low')}</option>
              <option value="medium">{t('medium')}</option>
              <option value="high">{t('high')}</option>
            </select>
          </div>

          {/* Assigned To Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('assignedTo')}</label>
            <select
              value={filters.assigned_to}
              onChange={(e) => setFilters({ ...filters, assigned_to: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:bg-gray-800"
            >
              <option value="">{t('all')}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('dateRange')}</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input
                  type="text"
                  placeholder="GG.AA.YYYY"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:bg-gray-800"
                />
                <span className="text-xs text-gray-500 mt-1">{t('from')}</span>
              </div>
              <div>
                <input
                  type="text"
                  placeholder="GG.AA.YYYY"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:bg-gray-800"
                />
                <span className="text-xs text-gray-500 mt-1">{t('to')}</span>
              </div>
            </div>
          </div>

          {/* Tags Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('tags')}</label>
            <input
              type="text"
              placeholder="tag1, tag2, tag3"
              value={filters.tags}
              onChange={(e) => setFilters({ ...filters, tags: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:bg-gray-800"
            />
            <span className="text-xs text-gray-500 mt-1">Virgülle ayırın</span>
          </div>

          {/* Active Filters Badge */}
          {Object.values(filters).some(v => v) && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <Filter className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-600 dark:text-blue-400">
                {Object.values(filters).filter(v => v).length} aktif filtre
              </span>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 p-4 border-t bg-white dark:bg-gray-900 space-y-2">
          {!showSaveDialog ? (
            <>
              <button
                onClick={() => setShowSaveDialog(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <Save className="w-4 h-4" />
                {t('saveFilter')}
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleClear}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  {t('clearFilters')}
                </button>
                <button
                  onClick={handleApply}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('applyFilters')}
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <input
                type="text"
                placeholder={t('filterName')}
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none dark:bg-gray-800"
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    setShowSaveDialog(false);
                    setFilterName('');
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleSaveFilter}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
